import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Select from '../../components/common/Select';
import Input from '../../components/common/Input';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import userService from '../../api/services/userService';
import { handleApiError, getApprovalStatusColor } from '../../utils/helpers';

const UsersPage = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    approvalStatus: '',
    role: '',
    search: ''
  });

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [filters, isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only send non-empty parameters
      const params = {
        page: 1,
        pageSize: 1000
      };
      
      if (filters.approvalStatus && filters.approvalStatus !== '') {
        params.approvalStatus = filters.approvalStatus;
      }
      if (filters.role && filters.role !== '') {
        params.role = filters.role;
      }
      if (filters.search && filters.search.trim() !== '') {
        params.search = filters.search.trim();
      }

      const response = await userService.getAllUsers(params);
      
      if (response.success) {
        // Backend returns: { success: true, message: "...", data: [...users array], pagination: {...} }
        const usersData = Array.isArray(response.data) ? response.data : [];
        setUsers(usersData);
      }
    } catch (err) {
      console.error('Fetch Users Error:', err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (selectedUser) => {
    setSelectedUser(selectedUser);
    setActionType('approve');
    setActionDialogOpen(true);
  };

  const handleRejectClick = (selectedUser) => {
    setSelectedUser(selectedUser);
    setActionType('reject');
    setActionDialogOpen(true);
  };

  const handleDeleteClick = (selectedUser) => {
    setSelectedUser(selectedUser);
    setActionType('delete');
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    try {
      if (actionType === 'approve') {
        const response = await userService.approveUser(selectedUser.id);
        if (response.success) {
          success('User approved successfully!');
          fetchUsers();
        }
      } else if (actionType === 'reject') {
        const response = await userService.rejectUser(selectedUser.id, 'Invalid documents or requirements not met');
        if (response.success) {
          success('User rejected successfully!');
          fetchUsers();
        }
      } else if (actionType === 'delete') {
        const response = await userService.deleteUser(selectedUser.id);
        if (response.success) {
          success('User deleted successfully!');
          fetchUsers();
        }
      }
      
      setActionDialogOpen(false);
      setSelectedUser(null);
      setActionType(null);
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      approvalStatus: '',
      role: '',
      search: ''
    });
  };

  // Statistics
  const stats = {
    total: users.length,
    pending: users.filter(u => u.approvalStatus === 'PENDING').length,
    approved: users.filter(u => u.approvalStatus === 'APPROVED').length,
    rejected: users.filter(u => u.approvalStatus === 'REJECTED').length,
    superAdmins: users.filter(u => u.role === 'SUPER_ADMIN').length,
    companyAdmins: users.filter(u => u.role === 'COMPANY_ADMIN').length
  };

  if (loading) {
    return (
      <DashboardLayout title="User Management">
        <div className="flex items-center justify-center min-h-96">
          <Loader size="lg" text="Loading users..." />
        </div>
      </DashboardLayout>
    );
  }

  // Only Super Admins can access this page
  if (!isSuperAdmin) {
    return (
      <DashboardLayout title="User Management">
        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-gray-500">Only Super Admins can access user management</p>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="User Management"
      subtitle="Manage user registrations, approvals, and permissions"
    >
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className="border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Approval</p>
              <p className="text-3xl font-bold text-gray-900">{stats.pending}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Approved</p>
              <p className="text-3xl font-bold text-gray-900">{stats.approved}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Rejected</p>
              <p className="text-3xl font-bold text-gray-900">{stats.rejected}</p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            label="Search"
            placeholder="Search by name, email, or phone"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />

          <Select
            label="Approval Status"
            value={filters.approvalStatus}
            onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' }
            ]}
          />

          <Select
            label="Role"
            value={filters.role}
            onChange={(e) => handleFilterChange('role', e.target.value)}
            options={[
              { value: '', label: 'All Roles' },
              { value: 'SUPER_ADMIN', label: 'Super Admin' },
              { value: 'COMPANY_ADMIN', label: 'Company Admin' }
            ]}
          />

          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {error && <ErrorMessage message={error} onRetry={fetchUsers} className="mb-6" />}

      {users.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mt-2">No users found</h3>
            <p className="text-gray-600 mt-2">
              {filters.search || filters.approvalStatus || filters.role
                ? 'No users match your current filters'
                : 'There are no user registrations to review'}
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((userItem) => {
                  const isCurrentUser = userItem.id === user.id;
                  
                  return (
                    <tr key={userItem.id} className={isCurrentUser ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                              {userItem.firstName?.[0]}{userItem.lastName?.[0]}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              {userItem.firstName} {userItem.lastName}
                              {isCurrentUser && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                  You
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{userItem.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{userItem.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          userItem.role === 'SUPER_ADMIN' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {userItem.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Company Admin'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getApprovalStatusColor(userItem.approvalStatus)}`}>
                          {userItem.approvalStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {isCurrentUser ? (
                          <span className="text-gray-400">Current User</span>
                        ) : (
                          <div className="flex gap-2">
                            {userItem.approvalStatus === 'PENDING' && (
                              <>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleApproveClick(userItem)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleRejectClick(userItem)}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {userItem.approvalStatus === 'APPROVED' && (
                              <span className="text-green-600 font-medium">✓ Approved</span>
                            )}
                            {userItem.approvalStatus === 'REJECTED' && (
                              <span className="text-red-600 font-medium">✗ Rejected</span>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(userItem)}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Action Confirmation Dialog */}
      <ConfirmDialog
        isOpen={actionDialogOpen}
        onClose={() => {
          setActionDialogOpen(false);
          setSelectedUser(null);
          setActionType(null);
        }}
        onConfirm={handleAction}
        title={
          actionType === 'approve' 
            ? 'Approve User' 
            : actionType === 'reject'
            ? 'Reject User'
            : 'Delete User'
        }
        message={
          actionType === 'approve'
            ? `Are you sure you want to approve ${selectedUser?.firstName} ${selectedUser?.lastName}? They will gain access to the system.`
            : actionType === 'reject'
            ? `Are you sure you want to reject ${selectedUser?.firstName} ${selectedUser?.lastName}? They will be notified of the rejection.`
            : `Are you sure you want to permanently delete ${selectedUser?.firstName} ${selectedUser?.lastName}? This action cannot be undone.`
        }
        confirmText={
          actionType === 'approve' 
            ? 'Approve' 
            : actionType === 'reject'
            ? 'Reject'
            : 'Delete'
        }
        variant={
          actionType === 'approve' 
            ? 'success' 
            : 'danger'
        }
      />
    </DashboardLayout>
  );
};

export default UsersPage;